import { equals, expect, test } from "@benchristel/taste"
import { PollCRDT } from "./PollCRDT"
import { withCandidate } from "./transformers"

/**
 * Parse a raw text blob into a list of candidate content strings.
 * Splits on newlines, trims whitespace, drops empty lines and duplicates.
 */
export function parseCandidateList(text: string): string[] {
  const seen = new Set<string>()
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => {
      if (!line || seen.has(line)) return false
      seen.add(line)
      return true
    })
}

/**
 * Apply a list of parsed candidate strings to a poll.
 * `makeID` is injectable so tests can supply deterministic IDs.
 */
export function withImportedCandidates(
  lines: string[],
  makeID: () => string,
  poll: PollCRDT,
): PollCRDT {
  return lines.reduce(
    (p, content) => withCandidate({ id: makeID(), content }, p),
    poll,
  )
}

// --- Tests ---

test("parseCandidateList", {
  "returns empty array for empty string"() {
    expect(parseCandidateList(""), equals, [])
  },

  "returns empty array for whitespace-only string"() {
    expect(parseCandidateList("   \n  \n"), equals, [])
  },

  "parses a single candidate"() {
    expect(parseCandidateList("nachos"), equals, ["nachos"])
  },

  "parses newline-separated candidates"() {
    expect(parseCandidateList("nachos\npizza\ntacos"), equals, [
      "nachos",
      "pizza",
      "tacos",
    ])
  },

  "trims leading and trailing whitespace from each candidate"() {
    expect(parseCandidateList("  nachos  \n  pizza  "), equals, [
      "nachos",
      "pizza",
    ])
  },

  "ignores blank lines"() {
    expect(parseCandidateList("nachos\n\npizza\n\ntacos"), equals, [
      "nachos",
      "pizza",
      "tacos",
    ])
  },

  "deduplicates identical candidates"() {
    expect(parseCandidateList("nachos\npizza\nnachos"), equals, [
      "nachos",
      "pizza",
    ])
  },
})

test("withImportedCandidates", {
  "adds no candidates for empty list"() {
    const poll: PollCRDT = { title: { value: "", generation: 0 }, initial: false, candidates: {}, ballots: {} }
    const result = withImportedCandidates([], () => "id-1", poll)
    expect(Object.keys(result.candidates).length, equals, 0)
  },

  "adds each candidate with IDs from the factory"() {
    const poll: PollCRDT = { title: { value: "", generation: 0 }, initial: false, candidates: {}, ballots: {} }
    let n = 0
    const makeID = () => `id-${++n}`
    const result = withImportedCandidates(["nachos", "pizza"], makeID, poll)
    expect(result.candidates["id-1"], equals, "nachos")
    expect(result.candidates["id-2"], equals, "pizza")
  },

  "preserves existing candidates"() {
    const poll = withCandidate({ id: "existing", content: "tacos" }, {
      title: { value: "", generation: 0 }, initial: false, candidates: {}, ballots: {},
    })
    let n = 0
    const result = withImportedCandidates(["nachos"], () => `new-${++n}`, poll)
    expect(result.candidates["existing"], equals, "tacos")
    expect(result.candidates["new-1"], equals, "nachos")
  },
})
